/**
 * The client's one Assigned Adviser (Sept 2026). Onboarding and annual reviews
 * share a single adviser: `assignment.assignedAdviser` (+ `assignedAdviserName`).
 * The old AR adviser field (`assignment.assignedARAdviser`) is retired — nothing
 * writes it; it is only read as a fallback until the one-off migration
 * (scripts/current-adviser-migration-2026-09.cjs) folds it into the one field.
 *
 * Past advisers are manual rows on the client Advice History page:
 *   adviceDates.onboardingAdvisers[] / adviceDates.arAdvisers[]
 *   { id, adviserEmail, adviserName, fromDate, toDate, notes, createdAt/By, updatedAt/By }
 *
 * CommonJS on purpose (browser + Node 18 require it). No object spread, no ESM.
 */

const CURRENT_ADVISER_FIELDS = Object.freeze({
  email: 'assignment.assignedAdviser',
  name: 'assignment.assignedAdviserName',
});

const LEGACY_AR_ADVISER_FIELDS = Object.freeze({
  email: 'assignment.assignedARAdviser',
  name: 'assignment.assignedARAdviserName',
});

/** Workflow derived key for the Assigned Adviser; the two old keys are aliases. */
const CURRENT_ADVISER_DERIVED_KEY = 'current_adviser';
const LEGACY_ADVISER_DERIVED_KEYS = Object.freeze(['onboarding_adviser', 'ar_adviser']);
const ADVISER_DERIVED_KEYS = Object.freeze([CURRENT_ADVISER_DERIVED_KEY].concat(LEGACY_ADVISER_DERIVED_KEYS));
const CURRENT_ADVISER_LABEL = 'Assigned Adviser';

function str(value) {
  return value == null ? '' : String(value).trim();
}

function normAdviserEmail(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function isAdviserDerivedKey(key) {
  return ADVISER_DERIVED_KEYS.indexOf(str(key)) !== -1;
}

/**
 * @returns {{ email: string, name: string }} email lower-cased ('' when unassigned)
 */
function getCurrentAdviser(client) {
  const assignment = (client && client.assignment) || {};
  const email = normAdviserEmail(assignment.assignedAdviser);
  if (email) return { email: email, name: str(assignment.assignedAdviserName) };
  const legacyEmail = normAdviserEmail(assignment.assignedARAdviser);
  if (legacyEmail) return { email: legacyEmail, name: str(assignment.assignedARAdviserName) };
  return { email: '', name: '' };
}

function getCurrentAdviserEmail(client) {
  return getCurrentAdviser(client).email;
}

const PAST_ADVISER_KINDS = Object.freeze(['onboarding', 'annualReview']);
const PAST_ADVISER_PATHS = Object.freeze({
  onboarding: 'adviceDates.onboardingAdvisers',
  annualReview: 'adviceDates.arAdvisers',
});
const ADVISER_HISTORY_KEYS = Object.freeze(['adviserEmail', 'adviserName', 'fromDate', 'toDate', 'notes']);

function getPastAdvisers(client, kind) {
  const bucket = (client && client.adviceDates) || {};
  const rows = kind === 'annualReview' ? bucket.arAdvisers : bucket.onboardingAdvisers;
  return Array.isArray(rows) ? rows.filter(function (row) { return row && typeof row === 'object'; }) : [];
}

/** Newest first: by toDate, then fromDate; undated rows last. */
function sortPastAdvisers(rows) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  function key(row) { return str(row && (row.toDate || row.fromDate)); }
  list.sort(function (a, b) {
    const ka = key(a);
    const kb = key(b);
    if (ka === kb) return 0;
    if (!ka) return 1;
    if (!kb) return -1;
    return ka < kb ? 1 : -1;
  });
  return list;
}

module.exports = {
  CURRENT_ADVISER_FIELDS,
  LEGACY_AR_ADVISER_FIELDS,
  CURRENT_ADVISER_DERIVED_KEY,
  LEGACY_ADVISER_DERIVED_KEYS,
  ADVISER_DERIVED_KEYS,
  CURRENT_ADVISER_LABEL,
  PAST_ADVISER_KINDS,
  PAST_ADVISER_PATHS,
  ADVISER_HISTORY_KEYS,
  normAdviserEmail,
  isAdviserDerivedKey,
  getCurrentAdviser,
  getCurrentAdviserEmail,
  getPastAdvisers,
  sortPastAdvisers,
};
